import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BALANCE_QUERY_KEY,
  TRANSACTIONS_PREFIX,
  useBalance,
  useTopupStatus,
} from '../api/useBilling.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { formatUsdCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';

/**
 * `/billing/topup/return` — landing page after ZPay's `return_url`
 * redirect (Epic #84 W4 P9).
 *
 * ZPay appends `?order_id=<id>` (and sometimes `&status=`). We treat
 * `order_id` as the source of truth and GET
 * `/v1/billing/topup/:order_id` to find the current status — the query
 * string status is suggestive but not trusted.
 *
 * State machine (status → render):
 *
 *   missing-order   → "no order id in URL" error screen with a link back
 *   pending         → spinner + poll every 3s; after 60s switch to the
 *                     "takes longer than usual" screen (polling stops
 *                     via `enabled=false` on the status hook).
 *   paid            → success screen: amount, new balance, auto-redirect
 *                     to /billing after 5s ("Return to dashboard" CTA
 *                     always available to skip the countdown).
 *   expired / failed / refunded
 *                   → error screen with "Try again" CTA linking back to
 *                     /billing/topup.
 *
 * Polling discipline: the `useTopupStatus` hook stops polling when
 * status leaves `pending`. We also gate it entirely off after the 60s
 * ceiling to avoid burning requests on unattended tabs.
 */

/**
 * After this many ms of continuous `pending` the return page stops
 * polling and shows the slow-mode card. Exported so tests can reference
 * the exact boundary value without re-hardcoding it.
 */
export const PENDING_CEILING_MS = 60_000;

/** Auto-redirect delay on the `paid` success card. */
export const PAID_REDIRECT_MS = 5_000;

type ViewState =
  | { readonly kind: 'missing-order' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'slow-pending' }
  | { readonly kind: 'paid' }
  | { readonly kind: 'expired' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'refunded' };

export function BillingTopupReturnPage(): JSX.Element {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();

  const orderId = search.get('order_id');
  const [slowMode, setSlowMode] = useState(false);
  const mountTsRef = useRef<number>(Date.now());

  const statusQ = useTopupStatus(orderId, { enabled: !slowMode });
  const balanceQ = useBalance();

  // Switch to slow-mode after 60s of continuous pending. The effect
  // doesn't need to re-arm on re-renders — one fixed-time ceiling
  // captured at mount.
  useEffect(() => {
    if (orderId === null || orderId.length === 0) {
      return;
    }
    const elapsed = Date.now() - mountTsRef.current;
    const remaining = Math.max(0, PENDING_CEILING_MS - elapsed);
    const handle = setTimeout(() => {
      setSlowMode(true);
    }, remaining);
    return () => clearTimeout(handle);
  }, [orderId]);

  const view: ViewState = (() => {
    if (orderId === null || orderId.length === 0) {
      return { kind: 'missing-order' };
    }
    if (statusQ.isLoading || statusQ.data === undefined) {
      return { kind: 'loading' };
    }
    const status = statusQ.data.status;
    if (status === 'paid') {
      return { kind: 'paid' };
    }
    if (status === 'expired') {
      return { kind: 'expired' };
    }
    if (status === 'failed') {
      return { kind: 'failed' };
    }
    if (status === 'refunded') {
      return { kind: 'refunded' };
    }
    // status === 'pending'
    return slowMode ? { kind: 'slow-pending' } : { kind: 'pending' };
  })();

  // Invalidate balance + transactions the moment we see `paid` so the
  // /billing view behind this page reflects the new credit.
  const paidInvalidatedRef = useRef(false);
  useEffect(() => {
    if (view.kind !== 'paid' || paidInvalidatedRef.current) {
      return;
    }
    paidInvalidatedRef.current = true;
    void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
  }, [view.kind, qc]);

  // Auto-redirect to /billing after the configured delay on a paid
  // order. The CTA gives the user a manual escape hatch.
  useEffect(() => {
    if (view.kind !== 'paid') {
      return;
    }
    const handle = setTimeout(() => {
      navigate('/billing', { replace: true });
    }, PAID_REDIRECT_MS);
    return () => clearTimeout(handle);
  }, [view.kind, navigate]);

  return (
    <section data-testid="billing-topup-return-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('billing.topup_return.page.title')}
        </h2>
        <p className="text-sm text-outline">
          {orderId !== null && orderId.length > 0 ? (
            <>
              {t('billing.topup_return.page.order_label')}
              <span className="font-mono text-on-surface-variant">{orderId}</span>
            </>
          ) : (
            t('billing.topup_return.page.waiting_order_id')
          )}
        </p>
      </header>

      {view.kind === 'missing-order' ? <MissingOrderCard /> : null}
      {view.kind === 'loading' ? <LoadingCard /> : null}
      {view.kind === 'pending' ? <PendingCard /> : null}
      {view.kind === 'slow-pending' ? <SlowPendingCard orderId={orderId ?? ''} /> : null}
      {view.kind === 'paid' ? (
        <PaidCard
          amountUsdCents={statusQ.data?.amountUsdCents ?? 0}
          appliedRate={statusQ.data?.appliedRate ?? null}
          amountCnyCents={statusQ.data?.amountCnyCents ?? 0}
          newBalanceUsdCents={balanceQ.data?.balanceUsdCents}
          newBalanceLoading={balanceQ.isLoading}
        />
      ) : null}
      {view.kind === 'expired' ? <TerminalErrorCard kind="expired" /> : null}
      {view.kind === 'failed' ? <TerminalErrorCard kind="failed" /> : null}
      {view.kind === 'refunded' ? <TerminalErrorCard kind="refunded" /> : null}
    </section>
  );
}

function LoadingCard(): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="billing-topup-return-loading"
      className="flex flex-col gap-3 rounded-xl bg-surface-container p-6"
    >
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-3 w-56" />
      <span className="sr-only">{t('billing.topup_return.loading.aria')}</span>
    </article>
  );
}

function MissingOrderCard(): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="billing-topup-return-missing"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('billing.topup_return.missing.title')}
        </h3>
      </header>
      <p className="text-sm text-outline">{t('billing.topup_return.missing.body')}</p>
      <div className="flex">
        <Link
          to="/billing"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow"
        >
          {t('billing.topup_return.missing.cta')}
        </Link>
      </div>
    </article>
  );
}

function PendingCard(): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="billing-topup-return-pending"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      role="status"
    >
      <header className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full bg-tertiary animate-pulse-tertiary"
        />
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('billing.topup_return.pending.title')}
        </h3>
      </header>
      <p className="text-sm text-on-surface">{t('billing.topup_return.pending.body')}</p>
    </article>
  );
}

interface SlowPendingCardProps {
  readonly orderId: string;
}

function SlowPendingCard({ orderId }: SlowPendingCardProps): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="billing-topup-return-slow"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('billing.topup_return.slow.title')}
        </h3>
      </header>
      <p className="text-sm text-on-surface">{t('billing.topup_return.slow.body')}</p>
      <p className="text-xs text-outline">
        {t('billing.topup_return.slow.reference')}{' '}
        <span className="font-mono text-on-surface-variant">{orderId}</span>
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Link
          to="/billing"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
        >
          {t('billing.topup_return.slow.cta.back')}
        </Link>
        <a
          href="mailto:support@nadoutong.org"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow"
          data-testid="billing-topup-return-support"
        >
          {t('billing.topup_return.slow.cta.support')}
        </a>
      </div>
    </article>
  );
}

interface PaidCardProps {
  readonly amountUsdCents: number;
  readonly amountCnyCents: number;
  readonly appliedRate: number | null;
  readonly newBalanceUsdCents: number | undefined;
  readonly newBalanceLoading: boolean;
}

function PaidCard({
  amountUsdCents,
  amountCnyCents,
  appliedRate,
  newBalanceUsdCents,
  newBalanceLoading,
}: PaidCardProps): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="billing-topup-return-paid"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      role="status"
    >
      <header className="flex items-center gap-2">
        <span aria-hidden="true" className="material-symbols-outlined text-[20px] text-primary">
          check_circle
        </span>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
          {t('billing.topup_return.paid.title')}
        </h3>
      </header>

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('billing.topup_return.paid.credited')}
          </dt>
          <dd
            className="font-mono text-lg text-primary"
            data-testid="billing-topup-return-credited"
          >
            {formatUsdCents(amountUsdCents, true)}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('billing.topup_return.paid.charged')}
          </dt>
          <dd
            className="font-mono text-sm text-on-surface-variant"
            data-testid="billing-topup-return-charged-cny"
          >
            {formatCnyCentsDisplay(amountCnyCents)}
            {appliedRate !== null ? (
              <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-outline">
                {t('billing.topup_return.paid.at_rate', { rate: appliedRate.toFixed(2) })}
              </span>
            ) : null}
          </dd>
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <dt className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {t('billing.topup_return.paid.new_balance')}
          </dt>
          <dd
            className="font-mono text-2xl text-primary"
            data-testid="billing-topup-return-balance"
          >
            {newBalanceLoading
              ? t('billing.topup_return.paid.balance_loading')
              : newBalanceUsdCents !== undefined
                ? formatUsdCents(newBalanceUsdCents)
                : '—'}
          </dd>
        </div>
      </dl>

      <p className="text-xs text-outline">{t('billing.topup_return.paid.redirect_hint')}</p>
      <div className="flex">
        <Link
          to="/billing"
          data-testid="billing-topup-return-cta"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow"
        >
          {t('billing.topup_return.paid.cta')}
        </Link>
      </div>
    </article>
  );
}

interface TerminalErrorCardProps {
  readonly kind: 'expired' | 'failed' | 'refunded';
}

function TerminalErrorCard({ kind }: TerminalErrorCardProps): JSX.Element {
  const t = useT();
  const headline =
    kind === 'expired'
      ? t('billing.topup_return.terminal.expired.title')
      : kind === 'failed'
        ? t('billing.topup_return.terminal.failed.title')
        : t('billing.topup_return.terminal.refunded.title');
  const body =
    kind === 'expired'
      ? t('billing.topup_return.terminal.expired.body')
      : kind === 'failed'
        ? t('billing.topup_return.terminal.failed.body')
        : t('billing.topup_return.terminal.refunded.body');
  return (
    <article
      data-testid={`billing-topup-return-${kind}`}
      className="flex flex-col gap-4 rounded-xl bg-error-container p-6 text-on-error-container"
      role="alert"
    >
      <header className="flex items-center gap-2">
        <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
          error
        </span>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em]">{headline}</h3>
      </header>
      <p className="text-sm">{body}</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Link
          to="/billing/topup"
          data-testid="billing-topup-return-retry"
          className="inline-flex items-center justify-center rounded-lg bg-on-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-error-container transition-colors hover:bg-error"
        >
          {t('billing.topup_return.terminal.cta.retry')}
        </Link>
        <Link
          to="/billing"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
        >
          {t('billing.topup_return.terminal.cta.back')}
        </Link>
      </div>
    </article>
  );
}

function formatCnyCentsDisplay(cents: number): string {
  return `¥ ${(cents / 100).toFixed(2)}`;
}
